/**
 * The rivals screen (F2): who is out there, and where you stand with each.
 *
 * Before Phase 10's diplomacy there are two states, war and peace, and three things to
 * do about them: declare war, offer peace, answer an offer. Every row also says how
 * the two armies compare — the same public figure the AI uses to decide whether it
 * could win (ADR-0049), so the player sees exactly the comparison a rival is making.
 */

import { describeTerms } from '../core/diplomacy/peace.js';
import type { CivilizationDef } from '../../content/schema/index.js';
import type { Engine } from '../core/engine.js';
import {
  atWar,
  militaryStrength,
  offerCooldown,
  relationOf,
  truceLeft,
  warLosses,
} from '../core/diplomacy/index.js';
import { VIS_VISIBLE } from '../core/state/types.js';
import { civCulture } from '../core/culture/index.js';
import { scoreOf } from '../core/score.js';
import { wondersBuilt } from '../core/cities/wonders.js';
import { governmentOf, inAnarchy } from '../core/government/index.js';
import { attitude } from '../core/diplomacy/attitude.js';
import { inContact } from '../core/diplomacy/borders.js';
import { treatyLeft } from '../core/diplomacy/index.js';
import type { DealItem } from '../core/state/types.js';
import { describeTrait, describeUnique, traitNames } from './civ-text.js';

export interface RivalsScreen {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  refresh(): void;
}

export interface RivalsScreenOptions {
  getEngine: () => Engine;
  /** Ask before declaring war; resolves true to go ahead. */
  confirmWar: (civId: number) => Promise<boolean>;
  /** Open the deal table with a rival. */
  talk: (civId: number) => void;
  /** Open the peace table with an enemy (ADR-0071). */
  peace: (civId: number) => void;
  /** Something changed the game; the map and panels should catch up. */
  onChanged: () => void;
  log: (message: string, kind?: 'ok' | 'err' | 'note') => void;
}

/** Rivals this civilisation has met: seen a city or a unit of, or has history with. */
export function metRivals(engine: Engine): number[] {
  const { state } = engine;
  const me = engine.activeCiv;
  const met = new Set<number>();
  const memory = state.memory[me];
  if (memory) for (const city of Object.values(memory.cities)) if (city.owner !== me) met.add(city.owner);
  const vision = state.vision[me];
  for (const unit of Object.values(state.units)) {
    if (unit.owner === me) continue;
    if (vision && vision[unit.row * state.map.width + unit.col] === VIS_VISIBLE) met.add(unit.owner);
  }
  for (const r of state.relations) {
    if (r.a === me) met.add(r.b);
    if (r.b === me) met.add(r.a);
  }
  return [...met].filter((id) => state.civs[id]).sort((a, b) => a - b);
}

export function createRivalsScreen(options: RivalsScreenOptions): RivalsScreen {
  const root = document.createElement('div');
  root.className = 'cities-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="cities-panel">
      <header>
        <h2>Rivals</h2>
        <div class="cs-sub" id="rv-sub"></div>
        <button id="rv-close" title="Close (Esc)">Close</button>
      </header>
      <div class="cts-body"><table id="rv-table" class="rv-table"></table><div id="rv-wonders" class="rv-wonders"></div></div>
    </div>
  `;
  document.body.append(root);
  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;

  function compare(ours: number, theirs: number): { text: string; flag: string } {
    if (theirs >= ours * 1.5) return { text: 'much stronger than you', flag: 'bad' };
    if (theirs > ours * 1.15) return { text: 'stronger than you', flag: 'soon' };
    if (theirs * 1.5 <= ours) return { text: 'much weaker than you', flag: '' };
    if (theirs * 1.15 < ours) return { text: 'weaker than you', flag: '' };
    return { text: 'about your equal', flag: '' };
  }

  function refresh(): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    // Hovering a rival's traits says what they mean, and what their unique unit is.
    const traitTitle = (def: CivilizationDef): string => {
      const unique = describeUnique(content, def);
      return [
        ...def.traitIds.flatMap((id) => describeTrait(content, content.traits.get(id))),
        ...(unique ? [`Unique unit: ${unique.name}, in place of the ${unique.replaces} — ${unique.line}`] : []),
      ].join('\n');
    };
    const me = engine.activeCiv;
    const met = metRivals(engine);
    const total = Object.values(state.civs).filter((c) => c.id !== me).length;
    const ours = militaryStrength(state, content, me);
    const myCulture = civCulture(state, content, me);
    const reputation = state.civs[me]?.reputation ?? 0;
    $('rv-sub').textContent =
      `Your score: ${scoreOf(state, content, me).total} · your army's strength: ${ours.toFixed(1)} · your culture: ${myCulture.total} (+${myCulture.perTurn} a turn) · ` +
      (reputation < 0 ? `your word is doubted (${Math.round(reputation)}) · ` : '') +
      `${met.length} of ${total} rival${total === 1 ? '' : 's'} met`;
    drawWonders(met);

    if (met.length === 0) {
      $('rv-table').innerHTML =
        '<tr><td class="dim">You have not met anyone yet. Explore — a rival is known once you have seen one of its cities or units.</td></tr>';
      return;
    }

    const header = '<tr><th>Civilisation</th><th>Standing</th><th>What they think of you</th><th>Score</th><th>Their army</th><th>Culture</th><th>This war</th><th></th></tr>';
    const rows = met
      .map((id) => {
        const civ = state.civs[id]!;
        const def = content.civilizations.get(civ.civType);
        const relation = relationOf(state, me, id);
        const swatch = `<span class="rv-swatch" style="background:${civ.colour}"></span>`;
        const temper = civ.personality >= 0 ? content.personalities.get(civ.personality) : null;
        const name =
          `${swatch}<b>${escapeHtml(civ.name)}</b> <span class="dim">${escapeHtml(def.leader)}</span>` +
          `<div class="rv-temper" title="${escapeHtml(traitTitle(def))}">${escapeHtml(traitNames(content, def))}</div>` +
          (temper ? `<div class="rv-temper"><b>${escapeHtml(temper.name)}</b> <span class="dim">— ${escapeHtml(temper.summary)}</span></div>` : '') +
          `<div class="rv-temper dim">${inAnarchy(civ) ? 'In revolution' : escapeHtml(governmentOf(content, civ).name)}</div>`;
        if (civ.defeated) return `<tr><td>${name}</td><td class="dim" colspan="7">Destroyed</td></tr>`;

        let standing: string;
        let standingFlag = '';
        let record = '<span class="dim">—</span>';
        let actions = '';
        if (atWar(state, me, id)) {
          const turns = state.meta.turn - relation!.since;
          standing = `At war · ${turns} turn${turns === 1 ? '' : 's'}`;
          standingFlag = 'bad';
          const { lost, inflicted } = warLosses(state, me, id);
          record = `lost ${lost} · inflicted ${inflicted} <span class="dim">shields</span>`;
          if (relation!.peaceOfferedBy === id) {
            const gives = relation!.peaceGive.length ? ` and give ${escapeHtml(describeTerms(state, content, relation!.peaceGive))}` : '';
            const asks = relation!.peaceGet.length ? `${gives ? ',' : ''} if you give ${escapeHtml(describeTerms(state, content, relation!.peaceGet))}` : '';
            actions = `<span class="soon">They offer peace${gives}${asks}</span> <button data-accept="${id}" class="primary">Accept</button> <button data-refuse="${id}">Refuse</button>`;
          } else {
            const wait = offerCooldown(state, content, me, id);
            actions =
              relation!.peaceOfferedBy === me
                ? '<span class="dim">Your offer awaits an answer</span>'
                : wait > 0
                  ? `<span class="dim">Can offer peace again in ${wait} turn${wait === 1 ? '' : 's'}</span>`
                  : `<button data-peace="${id}">Offer peace</button>`;
          }
        } else {
          const truce = truceLeft(state, me, id);
          const treaty = treatyLeft(state, me, id);
          standing =
            truce > 0
              ? `At peace · truce ${truce} turn${truce === 1 ? '' : 's'}`
              : treaty > 0
                ? `At peace · treaty ${treaty} turn${treaty === 1 ? '' : 's'}`
                : 'At peace';
          actions =
            (inContact(state, me, id) ? `<button data-talk="${id}" class="primary">Talk</button> ` : '') +
            (truce > 0 ? '' : `<button data-war="${id}" class="danger">Declare war</button>`);
        }
        // Agreements in force between you.
        const pacts: string[] = [];
        if (relation?.openBorders) pacts.push(`Open borders <button data-close="${id}" class="link" title="Close your borders to them">close</button>`);
        for (const enemy of relation?.alliances ?? []) pacts.push(`Allied against ${escapeHtml(state.civs[enemy]?.name ?? '?')}`);
        for (const p of state.payments) {
          if (p.from === me && p.to === id) pacts.push(`You pay ${p.amount}g a turn · ${p.until - state.meta.turn} left <button data-stop="${p.id}" class="link" title="Stopping a payment you promised costs you their trust, and everyone's">stop</button>`);
          if (p.from === id && p.to === me) pacts.push(`They pay you ${p.amount}g a turn · ${p.until - state.meta.turn} left`);
        }
        for (const g of state.grants) {
          const name = escapeHtml(content.resources.get(g.resource).name);
          if (g.from === me && g.to === id) pacts.push(`You supply ${name} · ${g.until - state.meta.turn} left`);
          if (g.from === id && g.to === me) pacts.push(`They supply you ${name} · ${g.until - state.meta.turn} left`);
        }
        if (pacts.length) standing += `<div class="rv-pacts">${pacts.map((p) => `<div>${p}</div>`).join('')}</div>`;

        const mood = attitude(state, content, id, me);
        const reasons = mood.reasons
          .slice(0, 6)
          .map((r) => `<li class="${r.value > 0 ? 'good' : 'bad'}">${r.value > 0 ? '+' : ''}${r.value} ${escapeHtml(r.label)}</li>`)
          .join('');
        const feeling = `<b class="att att-${mood.band.name.toLowerCase()}">${escapeHtml(mood.band.name)}</b> <span class="dim">(${mood.score > 0 ? '+' : ''}${mood.score})</span>${reasons ? `<ul class="rv-reasons">${reasons}</ul>` : '<div class="dim rv-reasons">Nothing between you yet.</div>'}`;

        const theirs = militaryStrength(state, content, id);
        const cmp = compare(ours, theirs);
        const culture = civCulture(state, content, id);
        return `<tr>
          <td>${name}</td>
          <td class="${standingFlag}">${standing}</td>
          <td>${feeling}</td>
          <td title="Public: see Score (F4) for how it is made up"><b>${scoreOf(state, content, id).total}</b></td>
          <td class="${cmp.flag}">${theirs.toFixed(1)} · ${cmp.text}</td>
          <td title="Culture earned by all their cities, and what they add a turn">${culture.total} <span class="dim">+${culture.perTurn}</span></td>
          <td>${record}</td>
          <td class="rv-actions">${actions}</td>
        </tr>${offerRows(id)}`;
      })
      .join('');
    $('rv-table').innerHTML = header + rows;

    function describe(items: DealItem[]): string {
      return items
        .map((item) => {
          switch (item.kind) {
            case 'tech':
              return escapeHtml(content.technologies.get(item.tech).name);
            case 'gold':
              return `${item.amount} gold`;
            case 'goldPerTurn':
              return `${item.amount} gold a turn for ${content.rules.diplomacy.paymentTurns} turns`;
            case 'openBorders':
              return 'open borders';
            case 'alliance':
              return `an alliance against ${escapeHtml(state.civs[item.against]?.name ?? '?')}`;
            case 'resource':
              return `${escapeHtml(content.resources.get(item.resource).name)} for ${content.rules.diplomacy.paymentTurns} turns`;
          }
        })
        .join(', ');
    }
    function offerRows(id: number): string {
      return state.offers
        .filter((o) => o.from === id && o.to === me)
        .map((o) => {
          const text = o.demand
            ? `<b>Demand:</b> they want ${describe(o.get)} — or else`
            : `<b>Offer:</b> they give ${describe(o.give) || 'nothing'}${o.get.length ? `, for ${describe(o.get)}` : ''}`;
          return `<tr class="rv-offer"><td colspan="8">${text}
            <button data-yes="${o.id}" class="primary">${o.demand ? 'Give in' : 'Accept'}</button>
            <button data-no="${o.id}">${o.demand ? 'Refuse' : 'Decline'}</button>
            <span class="dim">withdrawn at turn ${o.expires}</span></td></tr>`;
        })
        .join('');
    }

    const act = (selector: string, run: (id: number) => void | Promise<void>): void => {
      for (const button of root.querySelectorAll<HTMLButtonElement>(selector)) {
        button.addEventListener('click', () => {
          const id = Number(Object.values(button.dataset)[0]);
          void Promise.resolve(run(id)).then(() => {
            options.onChanged();
            refresh();
          });
        });
      }
    };

    act('button[data-talk]', (id) => options.talk(id));
    act('button[data-close]', (id) => {
      const result = engine.apply({ t: 'CancelAgreement', openBordersWith: id });
      options.log(result.ok ? `Your borders are closed to ${state.civs[id]!.name}.` : 'Nothing to close.', result.ok ? 'note' : 'err');
    });
    act('button[data-stop]', (id) => {
      const result = engine.apply({ t: 'CancelAgreement', payment: id });
      options.log(result.ok ? 'Payment stopped. Your word counts for less now.' : 'No such payment.', result.ok ? 'note' : 'err');
    });
    act('button[data-yes]', (id) => {
      const result = engine.apply({ t: 'AnswerOffer', offerId: id, accept: true });
      options.log(result.ok ? 'Agreed.' : 'That offer can no longer be carried out.', result.ok ? 'ok' : 'err');
    });
    act('button[data-no]', (id) => {
      const result = engine.apply({ t: 'AnswerOffer', offerId: id, accept: false });
      options.log(result.ok ? 'Refused.' : 'No such offer.', 'note');
    });
    act('button[data-war]', async (id) => {
      if (!(await options.confirmWar(id))) return;
      const result = engine.apply({ t: 'DeclareWar', on: id });
      options.log(result.ok ? `You are at war with ${state.civs[id]!.name}.` : 'War could not be declared.', result.ok ? 'note' : 'err');
    });
    act('button[data-peace]', (id) => options.peace(id));
    act('button[data-accept]', (id) => {
      if (engine.apply({ t: 'AnswerPeace', from: id, accept: true }).ok) options.log(`Peace with ${state.civs[id]!.name}.`, 'ok');
    });
    act('button[data-refuse]', (id) => {
      if (engine.apply({ t: 'AnswerPeace', from: id, accept: false }).ok) options.log(`You refuse ${state.civs[id]!.name}'s offer.`, 'note');
    });
  }

  /**
   * The wonders of the world: who holds each, and what is still to be won. A wonder is
   * world news, so every one is listed; the holder is named only if you have met them.
   */
  function drawWonders(met: number[]): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    const me = engine.activeCiv;
    const built = new Map(wondersBuilt(state, content).map((w) => [w.def.id, w.city]));
    const known = new Set([me, ...met]);
    const lines = content.buildings.all
      .filter((def) => def.wonder)
      .map((def) => {
        const city = built.get(def.id);
        let where = '<span class="dim">not yet built</span>';
        if (city) {
          const holder = state.civs[city.owner];
          where = known.has(city.owner)
            ? `${city.owner === me ? '<b>yours</b> — ' : `${escapeHtml(holder?.name ?? '')} — `}${escapeHtml(city.name)}`
            : 'a civilisation you have not met';
        }
        return `<tr><td title="${escapeHtml(def.description)}">★ ${escapeHtml(def.name)}</td><td>${where}</td></tr>`;
      })
      .join('');
    $('rv-wonders').innerHTML = lines ? `<h3>Wonders of the world</h3><table class="rv-table">${lines}</table>` : '';
  }

  function open(): void {
    root.hidden = false;
    refresh();
  }
  function close(): void {
    root.hidden = true;
  }
  $('rv-close').addEventListener('click', close);
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  return {
    open,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
    refresh,
  };
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
